/**
 * useActiveDataset — синхронизация ``project.active_dataset_id`` с Zustand.
 *
 * Phase 2.1 (Files UX Refactor 2026-08-06):
 * - При первом mount на /files/results (или смене проекта) нужно подтянуть
 *   с сервера, какой dataset активный для текущего projectId, и положить
 *   в ``useProjectStore.activeDatasetId``. Иначе после F5 / холодного старта
 *   бейдж «В работе» в ``FilesResultsPage`` / ``DatasetList`` не подсветится
 *   (раньше это был чисто client state, и он терялся).
 *
 * Контракт:
 * - Если ``projectId === null`` — возвращаем ``null`` без запроса.
 * - Если запрос ещё идёт — ``isLoading=true``.
 * - Если 404 / cross-tenant — store НЕ обновляется (старый active_dataset_id
 *   мог быть из другого проекта, не хотим его терять).
 * - Если запрос успешен — store обновляется только когда пришёл новый id,
 *   отличный от текущего (избегаем re-render-петель).
 */
import { useEffect, useRef } from 'react';

import { useProject } from '@/hooks/useProjects';
import { useProjectStore } from '@/store/projectStore';

export function useActiveDataset(projectId: string | null) {
  const { data, isLoading, error } = useProject(projectId);
  const setActive = useProjectStore((s) => s.setActiveDataset);

  // Ref, чтобы избежать re-fetch петель: useEffect ниже зависит от
  // ``data?.active_dataset_id``, но колбэк setActive стабилен.
  const lastSyncedRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (!projectId) {
      // Project сброшен — стираем локальное значение.
      if (lastSyncedRef.current !== null) {
        lastSyncedRef.current = null;
        setActive(null);
      }
      return;
    }
    if (!data) return; // ещё не пришёл
    const incoming = data.active_dataset_id ?? null;
    if (lastSyncedRef.current !== incoming) {
      lastSyncedRef.current = incoming;
      setActive(incoming);
    }
    // setActive стабилен из Zustand (один reference) — намеренно НЕ
    // включаем в deps, чтобы эффект не дёргался лишний раз.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, data?.active_dataset_id, data === undefined]);

  return {
    activeDatasetId: useProjectStore.getState().activeDatasetId,
    isLoading,
    error,
  };
}
